import { useState, useEffect, useCallback } from "react";
import { PageMeta } from "@/components/seo/PageMeta";
import confetti from "canvas-confetti";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { passwordSchema } from "@/lib/validation";
import { useBiometricAuth } from "@/hooks/useBiometricAuth";
import { useHaptics } from "@/hooks/useHaptics";
import { storeBiometricCredentials } from "@/utils/secureStorage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Fingerprint, ScanFace, Mail, Eye, EyeOff, Check, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useGuest } from "@/contexts/GuestContext";
import { LEGAL_CONFIG } from "@/lib/legalConfig";

import { SafeAreaSpacer } from "@/components/layout/SafeAreaSpacer";

const AuthSkeleton = () => (
  <div className="min-h-screen bg-gradient-rose-sky flex flex-col">
    <SafeAreaSpacer position="top" className="bg-gradient-rose-sky" />
    <div className="flex-1 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-card rounded-3xl p-8 card-shadow">
      <div className="text-center mb-6">
        <Skeleton className="w-20 h-20 mx-auto mb-4 rounded-full" />
        <Skeleton className="h-8 w-48 mx-auto mb-2" />
        <Skeleton className="h-4 w-32 mx-auto" />
      </div>
      
      <div className="space-y-4">
        <div>
          <Skeleton className="h-4 w-16 mb-2" />
          <Skeleton className="h-11 w-full rounded-xl" />
        </div>
        <div>
          <Skeleton className="h-4 w-20 mb-2" />
          <Skeleton className="h-11 w-full rounded-xl" />
        </div>
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-28" />
        </div>
        <Skeleton className="h-11 w-full rounded-xl" />
      </div>
      
      <div className="mt-6 space-y-3">
        <Skeleton className="h-4 w-40 mx-auto" />
        <Skeleton className="h-11 w-full rounded-xl" />
      </div>
    </div>
    </div>
    <SafeAreaSpacer position="bottom" className="bg-gradient-rose-sky" />
  </div>
);

const Auth = () => {
  const { enterGuestMode, pendingAction, clearPendingAction } = useGuest();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [identifier, setIdentifier] = useState("");
  
  // Simulate brief loading for smooth skeleton transition
  useEffect(() => {
    const timer = setTimeout(() => setPageLoading(false), 400);
    return () => clearTimeout(timer);
  }, []);
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetIdentifier, setResetIdentifier] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const navigate = useNavigate();
  const { isBiometricAvailable, isBiometricEnrolled, isAuthenticating, biometricType, autoBiometricEnabled, triggerBiometricAuth } = useBiometricAuth();
  const { lightTap, mediumTap, success, error: errorHaptic } = useHaptics();

  // Auto-trigger biometric auth on app launch for enrolled users (if enabled)
  // Tier 1: Wait 1500ms grace period AND skip if a manual auth is in flight
  // to prevent token-race conditions where parallel refresh calls invalidate each other.
  const [hasAutoTriggered, setHasAutoTriggered] = useState(false);
  useEffect(() => {
    if (
      !hasAutoTriggered &&
      autoBiometricEnabled &&
      isBiometricAvailable &&
      isBiometricEnrolled &&
      !isAuthenticating &&
      !pageLoading &&
      !loading // Don't race against a manual sign-in already in flight
    ) {
      setHasAutoTriggered(true);
      const timer = setTimeout(() => {
        // Re-check loading state right before firing — user may have started typing
        if (!loading) {
          triggerBiometricAuth();
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isBiometricAvailable, isBiometricEnrolled, isAuthenticating, pageLoading, hasAutoTriggered, autoBiometricEnabled, triggerBiometricAuth, loading]);

  // Get biometric label based on type
  const getBiometricLabel = () => {
    if (isAuthenticating) return "Authenticating...";
    const typeName = biometricType === 'faceId' ? 'Face ID' : biometricType === 'touchId' ? 'Touch ID' : 'Biometrics';
    if (!isBiometricEnrolled) return `${typeName} Not Enrolled`;
    return `Sign In with ${typeName}`;
  };

  // Validation helpers
  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidUsername = (name: string) => /^[a-zA-Z0-9_]{3,20}$/.test(name);
  const isValidPhone = (phone: string) => !phone || /^\+?[\d\s()-]{10,}$/.test(phone);
  
  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return { score: 0, label: "", color: "" };
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (pwd.length >= 12) score++;
    
    if (score <= 1) return { score, label: "Weak", color: "text-destructive" };
    if (score <= 2) return { score, label: "Fair", color: "text-orange-500" };
    if (score <= 3) return { score, label: "Good", color: "text-yellow-500" };
    return { score, label: "Strong", color: "text-green-500" };
  };

  const passwordStrength = getPasswordStrength(password);
  const emailValid = identifier ? isValidEmail(identifier) : null;
  const usernameValid = username ? isValidUsername(username) : null;
  const phoneValid = phoneNumber ? isValidPhone(phoneNumber) : null;

  const detectIdentifierType = (input: string): 'email' | 'phone' | 'username' => {
    if (input.includes('@')) return 'email';
    if (/^\+?[\d\s()-]+$/.test(input)) return 'phone';
    return 'username';
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    mediumTap();
    
    // Validate password for signup
    if (!isLogin) {
      const passwordValidation = passwordSchema.safeParse(password);
      if (!passwordValidation.success) {
        setPasswordError(passwordValidation.error.errors[0].message);
        return;
      }
      setPasswordError("");
    }
    
    setLoading(true);

    try {
      if (isLogin) {
        // Login - detect identifier type
        const identifierType = detectIdentifierType(identifier);
        let loginEmail = identifier;

        // If username or phone, look up the email via edge function (with 6s timeout)
        if (identifierType === 'username' || identifierType === 'phone') {
          console.log('[Auth] lookup-user-email start', new Date().toISOString(), { identifierType });
          try {
            const lookupPromise = supabase.functions.invoke('lookup-user-email', {
              body: { identifier }
            });
            const timeoutPromise = new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error('LOOKUP_TIMEOUT')), 6000)
            );
            const { data: lookupData, error: lookupError } = await Promise.race([
              lookupPromise,
              timeoutPromise,
            ]) as Awaited<typeof lookupPromise>;
            console.log('[Auth] lookup-user-email done', new Date().toISOString(), { hasError: !!lookupError, hasEmail: !!lookupData?.email });

            if (lookupError || !lookupData?.email) {
              setShowSignUpPrompt(true);
              setLoading(false);
              return;
            }
            loginEmail = lookupData.email;
          } catch (lookupErr: any) {
            console.error('[Auth] lookup-user-email failed', lookupErr);
            if (lookupErr?.message === 'LOOKUP_TIMEOUT') {
              toast.error("Login is slow right now. Please try your email instead.");
            } else {
              toast.error("Couldn't look up your account. Please try your email instead.");
            }
            setLoading(false);
            return;
          }
        }

        console.log('[Auth] signInWithPassword start', new Date().toISOString());
        const { data, error } = await supabase.auth.signInWithPassword({
          email: loginEmail,
          password,
        });
        console.log('[Auth] signInWithPassword done', new Date().toISOString(), { hasError: !!error, hasUser: !!data?.user });

        if (error) {
          if (error.message.includes("Invalid login credentials")) {
            setShowSignUpPrompt(true);
            setLoading(false);
            return;
          }
          throw error;
        }

        // Get user profile for personalized greeting
        console.log('[Auth] profile fetch start', new Date().toISOString());
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name, username')
          .eq('id', data.user?.id)
          .single();
        console.log('[Auth] profile fetch done', new Date().toISOString(), { hasProfile: !!profile });

        const displayName = profile?.full_name?.split(' ')[0] || profile?.username || 'Cowgirl';
        
        // Clear guest mode flag on successful login
        localStorage.removeItem('wellness_cowgirl_guest_mode');
        localStorage.removeItem('wellness_cowgirl_guest_tour_completed');
        
        // Handle remember me preference
        if (!rememberMe) {
          sessionStorage.setItem('wellness_cowgirl_session_only', 'true');
        } else {
          sessionStorage.removeItem('wellness_cowgirl_session_only');
        }
        
        // Store refresh token for biometric auth if in Despia environment
        if (data.session?.refresh_token && navigator.userAgent.includes("despia")) {
          storeBiometricCredentials(data.session.refresh_token);
          success();
        } else {
          success();
        }
        
        // Welcome back confetti burst
        confetti({
          particleCount: 60,
          spread: 50,
          origin: { y: 0.7 },
          colors: ['#E8998D', '#98D8C8', '#F7DC6F'],
          startVelocity: 25,
          gravity: 1.2,
        });
        
        toast.success(`Welcome back, ${displayName}!`, {
          description: "Good to see you again",
          duration: 3000,
        });
        
        // Check for pending action and redirect
        if (pendingAction) {
          navigate(pendingAction.route);
          clearPendingAction();
        } else {
          navigate("/");
        }
      } else {
        // Signup
        const { error } = await supabase.auth.signUp({
          email: identifier,
          password,
          options: {
            data: {
              username,
              full_name: fullName,
              phone_number: phoneNumber || null,
            },
            emailRedirectTo: `${window.location.origin}/`,
          },
        });
        
        if (error) throw error;
        
        // Clear guest mode flag on successful signup
        localStorage.removeItem('wellness_cowgirl_guest_mode');
        localStorage.removeItem('wellness_cowgirl_guest_tour_completed');
        
        success();
        
        // Celebration confetti!
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#E8998D', '#C9B1FF', '#98D8C8', '#F7DC6F', '#BB8FCE']
        });
        
        // Second burst for extra celebration
        setTimeout(() => {
          confetti({
            particleCount: 50,
            angle: 60,
            spread: 55,
            origin: { x: 0 },
            colors: ['#E8998D', '#C9B1FF', '#98D8C8']
          });
          confetti({
            particleCount: 50,
            angle: 120,
            spread: 55,
            origin: { x: 1 },
            colors: ['#E8998D', '#C9B1FF', '#98D8C8']
          });
        }, 250);
        
        toast.success("Account created! Welcome to Wellness Cowgirl. 🎉");
        
        // Check for pending action
        if (pendingAction) {
          // Store it temporarily for after onboarding
          sessionStorage.setItem('post_onboarding_route', pendingAction.route);
          clearPendingAction();
        }
        
        navigate("/onboarding");
      }
    } catch (err: any) {
      errorHaptic();
      console.error('[Auth] sign-in/up failed:', err);

      // ===== Track B: Out-of-band diagnostic =====
      const sbKeys: string[] = [];
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith('sb-')) sbKeys.push(k);
        }
      } catch {}
      const diag = {
        ts: new Date().toISOString(),
        host: window.location.hostname,
        errName: err?.name || 'Unknown',
        errMessage: String(err?.message || err || 'Unknown'),
        userAgent: navigator.userAgent,
        online: navigator.onLine,
        sbKeysPresent: sbKeys,
        swController: 'serviceWorker' in navigator ? !!navigator.serviceWorker.controller : false,
      };
      console.warn('[AUTH-DIAG]', diag);

      toast.error(err?.message || 'Sign in failed. Please try again.', {
        duration: 12000,
        description: "If this keeps happening, tap 'Reset login state' below.",
        action: {
          label: 'Copy diagnostic',
          onClick: () => {
            try {
              navigator.clipboard.writeText(JSON.stringify(diag, null, 2));
              toast.success('Diagnostic copied. Paste to support.');
            } catch {
              toast.error('Could not copy. Screenshot this screen.');
            }
          },
        },
      });
    } finally {
      setLoading(false);
    }
  };

  // ===== Track B: Manual recovery — clears all sb-* state and reloads =====
  const handleResetLoginState = () => {
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('sb-')) toRemove.push(k);
      }
      toRemove.forEach((k) => { try { localStorage.removeItem(k); } catch {} });
      try { sessionStorage.clear(); } catch {}
      toast.success('Login state cleared. Reloading…');
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      console.error('[Auth] reset failed:', e);
      toast.error('Reset failed. Try closing and reopening the app.');
    }
  };

  const handleSwitchToSignUp = () => {
    lightTap();
    setShowSignUpPrompt(false);
    setIsLogin(false);
    setPassword("");
    setPasswordError("");
    setPhoneNumber("");
    toast.info("Fill in your details to create an account!");
  };

  const handleForgotPassword = async () => {
    if (!resetIdentifier.trim()) {
      toast.error("Please enter your email, username, or phone number");
      return;
    }

    setResetLoading(true);
    try {
      const identifierType = detectIdentifierType(resetIdentifier);
      let resetEmail = resetIdentifier;

      // If username or phone, look up the email via edge function (with 6s timeout)
      if (identifierType === 'username' || identifierType === 'phone') {
        try {
          const lookupPromise = supabase.functions.invoke('lookup-user-email', {
            body: { identifier: resetIdentifier }
          });
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('LOOKUP_TIMEOUT')), 6000)
          );
          const { data: lookupData, error: lookupError } = await Promise.race([
            lookupPromise,
            timeoutPromise,
          ]) as Awaited<typeof lookupPromise>;

          if (lookupError || !lookupData?.email) {
            toast.error("Account not found. Please check your information.");
            setResetLoading(false);
            return;
          }
          resetEmail = lookupData.email;
        } catch (lookupErr: any) {
          if (lookupErr?.message === 'LOOKUP_TIMEOUT') {
            toast.error("Lookup timed out. Please use your email to reset.");
          } else {
            toast.error("Couldn't look up your account. Please use your email instead.");
          }
          setResetLoading(false);
          return;
        }
      }

      // Send password reset email
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast.success("Password reset email sent! Check your inbox.");
      setShowForgotPassword(false);
      setResetIdentifier("");
    } catch (error: any) {
      toast.error(error.message || "Failed to send reset email");
    } finally {
      setResetLoading(false);
    }
  };

  if (pageLoading) {
    return <AuthSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-rose-sky flex flex-col">
      <PageMeta
        title="Sign In or Join — Wellness Cowgirls"
        description="Sign in to Wellness Cowgirls or create a free account to discover San Antonio wellness events and meet your community."
        path="/auth"
      />
      <SafeAreaSpacer position="top" className="bg-gradient-rose-sky" />
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md bg-card rounded-3xl p-6 sm:p-8 card-shadow animate-fade-in">
        <div className="text-center mb-6" key={isLogin ? "login-header" : "signup-header"}>
          <img 
            src="/logo/wellness-logo.png" 
            alt="Wellness Cowgirl logo - lotus flower with cowboy hat"
            className="w-20 h-20 mx-auto mb-4 object-contain drop-shadow-lg"
          />
          <h1 className="sr-only">
            {isLogin ? "Sign in to Wellness Cowgirls" : "Create your Wellness Cowgirls account"}
          </h1>
          <p className="text-3xl font-bold mb-2 animate-fade-in" aria-hidden="true">
            {isLogin ? "Hey Cowgirl!" : "Join Wellness Cowgirls"}
          </p>
          <p className="text-muted-foreground animate-fade-in" style={{ animationDelay: "50ms" }}>
            {isLogin ? "Sign in to your account" : "Start your wellness journey"}
          </p>
        </div>

        <form onSubmit={handleAuth} className="space-y-4" key={isLogin ? "login-form" : "signup-form"}>
          {!isLogin && (
            <div className="space-y-4 animate-fade-in">
              <div>
                <Label htmlFor="username">Username</Label>
                <div className="relative">
                  <Input
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                    placeholder="cowgirl123"
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    required
                    className={`pr-10 ${username && (usernameValid ? "border-green-500 focus-visible:ring-green-500" : "border-destructive focus-visible:ring-destructive")}`}
                  />
                  {username && (
                    <div className={`absolute right-3 top-1/2 -translate-y-1/2 ${usernameValid ? "text-green-500" : "text-destructive"}`}>
                      {usernameValid ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    </div>
                  )}
                </div>
                {username && !usernameValid && (
                  <p className="text-xs text-destructive mt-1">3-20 characters, letters, numbers, underscores only</p>
                )}
              </div>
              <div>
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Sarah Martinez"
                  autoComplete="name"
                  autoCapitalize="words"
                />
              </div>
              <div>
                <Label htmlFor="phoneNumber">Phone (optional)</Label>
                <div className="relative">
                  <Input
                    id="phoneNumber"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="(210) 555-1234"
                    autoComplete="tel"
                    inputMode="tel"
                    className={phoneNumber ? (phoneValid ? "border-green-500 focus-visible:ring-green-500 pr-10" : "border-destructive focus-visible:ring-destructive pr-10") : ""}
                  />
                  {phoneNumber && (
                    <div className={`absolute right-3 top-1/2 -translate-y-1/2 ${phoneValid ? "text-green-500" : "text-destructive"}`}>
                      {phoneValid ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    </div>
                  )}
                </div>
                {phoneNumber && !phoneValid && (
                  <p className="text-xs text-destructive mt-1">Enter a valid phone number</p>
                )}
              </div>
            </div>
          )}
          
          <div>
            <Label htmlFor="identifier">
              {isLogin ? "Sign In" : "Email"}
            </Label>
            <div className="relative">
              <Input
                id="identifier"
                type={isLogin ? "text" : "email"}
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder={isLogin ? "Email, username, or phone" : "you@example.com"}
                autoComplete={isLogin ? "username" : "email"}
                autoCapitalize="none"
                autoCorrect="off"
                inputMode={!isLogin || identifier.includes('@') ? "email" : "text"}
                required
                className={!isLogin && identifier ? (emailValid ? "border-green-500 focus-visible:ring-green-500 pr-10" : "border-destructive focus-visible:ring-destructive pr-10") : ""}
              />
              {!isLogin && identifier && (
                <div className={`absolute right-3 top-1/2 -translate-y-1/2 ${emailValid ? "text-green-500" : "text-destructive"}`}>
                  {emailValid ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                </div>
              )}
            </div>
            {!isLogin && identifier && !emailValid && (
              <p className="text-xs text-destructive mt-1">Enter a valid email address</p>
            )}
          </div>
          
          <div>
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (!isLogin && passwordError) {
                    const validation = passwordSchema.safeParse(e.target.value);
                    if (validation.success) setPasswordError("");
                  }
                }}
                placeholder="••••••••"
                autoComplete={isLogin ? "current-password" : "new-password"}
                required
                minLength={6}
                className={`pr-10 ${!isLogin && password ? (passwordStrength.score >= 3 ? "border-green-500 focus-visible:ring-green-500" : passwordStrength.score >= 2 ? "border-yellow-500 focus-visible:ring-yellow-500" : "border-destructive focus-visible:ring-destructive") : ""} ${passwordError ? "border-destructive" : ""}`}
              />
              <button
                type="button"
                onClick={() => {
                  lightTap();
                  setShowPassword(!showPassword);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors min-w-6 min-h-6 flex items-center justify-center"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {!isLogin && password && (
              <div className="mt-2 space-y-1">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-300 ${
                        passwordStrength.score >= 4 ? "bg-green-500 w-full" :
                        passwordStrength.score >= 3 ? "bg-green-500 w-3/4" :
                        passwordStrength.score >= 2 ? "bg-yellow-500 w-1/2" :
                        "bg-destructive w-1/4"
                      }`}
                    />
                  </div>
                  <span className={`text-xs font-medium ${passwordStrength.color}`}>
                    {passwordStrength.label}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  8+ chars, number, special character, uppercase
                </p>
              </div>
            )}
            {passwordError && (
              <p className="text-xs text-destructive mt-1">{passwordError}</p>
            )}
          </div>
          
          {!isLogin && (
            <div className="flex items-start gap-2 pt-2">
              <input
                type="checkbox"
                id="terms"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-border"
              />
              <label htmlFor="terms" className="text-sm text-muted-foreground">
                I agree to the{" "}
                <a href={LEGAL_CONFIG.TERMS_OF_SERVICE_URL} className="text-primary hover:underline">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href={LEGAL_CONFIG.PRIVACY_POLICY_URL} className="text-primary hover:underline">
                  Privacy Policy
                </a>
              </label>
            </div>
          )}

          {isLogin && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => {
                    lightTap();
                    setRememberMe(e.target.checked);
                  }}
                  className="h-4 w-4 rounded border-border"
                />
                <label htmlFor="rememberMe" className="text-sm text-muted-foreground">
                  Remember me
                </label>
              </div>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="text-xs text-muted-foreground hover:text-foreground p-0 h-auto"
                onClick={() => {
                  lightTap();
                  setShowForgotPassword(true);
                }}
              >
                Forgot password?
              </Button>
            </div>
          )}

          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading || (!isLogin && !agreedToTerms)}
          >
            {loading ? "Loading..." : isLogin ? "Sign In" : "Create Account"}
          </Button>

          {isLogin && (
            <div className="text-center pt-1">
              <Button
                type="button"
                variant="link"
                size="sm"
                className="text-xs text-muted-foreground hover:text-foreground p-0 h-auto"
                onClick={handleResetLoginState}
              >
                Trouble signing in? Reset login state
              </Button>
            </div>
          )}

        </form>

        {/* Biometric Authentication Option (Login only) */}
        {isLogin && isBiometricAvailable && (
          <>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={triggerBiometricAuth}
              disabled={!isBiometricEnrolled || isAuthenticating}
            >
              {biometricType === 'faceId' ? (
                <ScanFace className="w-4 h-4 mr-2" />
              ) : (
                <Fingerprint className="w-4 h-4 mr-2" />
              )}
              {getBiometricLabel()}
            </Button>

            {!isBiometricEnrolled && (
              <p className="mt-2 text-xs text-muted-foreground text-center">
                Log in once with email and password to enable biometric sign-in.
              </p>
            )}
          </>
        )}

        <div className="mt-6 text-center space-y-3">
          <p className="text-sm text-muted-foreground">
            {isLogin ? "Don't have an account?" : "Already have an account?"}
          </p>
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => {
              lightTap();
              setIsLogin(!isLogin);
            }}
          >
            {isLogin ? "Create New Account" : "Sign In Instead"}
          </Button>
          
          {isLogin && (
            <>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or</span>
                </div>
              </div>

              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  lightTap();
                  enterGuestMode();
                }}
                className="w-full text-muted-foreground hover:text-foreground"
              >
                Continue as Guest
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Sign In Failed Dialog */}
      <AlertDialog open={showSignUpPrompt} onOpenChange={setShowSignUpPrompt}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-center">
              Sign In Failed
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center space-y-3">
              <p>We couldn't sign you in. This could mean:</p>
              <ul className="text-left text-sm space-y-1 pl-4">
                <li>• The password is incorrect</li>
                <li>• No account exists with this email/username</li>
              </ul>
              <p className="text-sm">Please double-check your credentials and try again.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2">
            <AlertDialogCancel className="w-full sm:w-auto">Try Again</AlertDialogCancel>
            <Button 
              variant="outline" 
              className="w-full sm:w-auto"
              onClick={() => {
                setShowSignUpPrompt(false);
                setShowForgotPassword(true);
              }}
            >
              Forgot Password?
            </Button>
            <AlertDialogAction onClick={handleSwitchToSignUp} className="w-full sm:w-auto">
              Create Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Forgot Password Dialog */}
      <AlertDialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 justify-center">
              <Mail className="w-5 h-5" />
              Reset Password
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center space-y-4">
              <p>
                Enter your email, username, or phone number and we'll send you a link to reset your password.
              </p>
              <div className="space-y-2">
                <Label htmlFor="reset-identifier" className="text-left block">
                  Email, Username, or Phone
                </Label>
                <Input
                  id="reset-identifier"
                  type="text"
                  value={resetIdentifier}
                  onChange={(e) => setResetIdentifier(e.target.value)}
                  placeholder="you@example.com, cowgirl123, or (210) 555-1234"
                  className="text-foreground"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel disabled={resetLoading}>Cancel</AlertDialogCancel>
            <Button onClick={handleForgotPassword} disabled={resetLoading}>
              {resetLoading ? "Sending..." : "Send Reset Link"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
      <SafeAreaSpacer position="bottom" className="bg-gradient-rose-sky" />
    </div>
  );
};

export default Auth;
